/**
 * Opt-in storefront pageview when analytics consent is in effect.
 *
 * On Pandectes storefronts, bootstrap does not queue viewPage before consent. This module
 * sends one Maestro viewPage per full page load when the storefront signals that
 * analytics consent is allowed.
 *
 * The signal is the dataLayer custom event Pandectes_Consent_Ready (emitted by GTM
 * after Pandectes consent updates). It is not a DOM event; this file listens via
 * dataLayer.push and replays entries already in the array at install time.
 */

/** dataLayer `event` value when analytics consent is allowed (GTM custom event). */
const PANDECTES_CONSENT_READY_EVENT = "Pandectes_Consent_Ready";

function getViewPagePayload() {
  return {
    path: window.location.pathname,
    url: window.location.href,
    market: document.documentElement.dataset?.countryCode,
  };
}

let consentPageviewInstalled = false;
let pageviewFiredThisLoad = false;
/** @type {((...args: unknown[]) => number) | null} */
let originalDataLayerPush = null;

/** Sends at most one viewPage per document load when Maestro is available. */
function firePageviewOnce() {
  if (pageviewFiredThisLoad) {
    return;
  }
  if (!window.maestro?.fireTrackEvent || !theme?.maestro?.viewPage) {
    return;
  }

  window.maestro.fireTrackEvent(theme.maestro.viewPage, getViewPagePayload());
  pageviewFiredThisLoad = true;
}

function handleDataLayerConsentReady(entry) {
  if (!entry || entry.event !== PANDECTES_CONSENT_READY_EVENT) {
    return;
  }

  firePageviewOnce();
}

/**
 * Subscribe to Pandectes_Consent_Ready on dataLayer.
 * Chains push so GTM keeps working; replays the array for events that occurred before install.
 */
function installDataLayerConsentReadyListener() {
  window.dataLayer = window.dataLayer || [];

  if (!originalDataLayerPush) {
    originalDataLayerPush = window.dataLayer.push.bind(window.dataLayer);
    window.dataLayer.push = function (...args) {
      const result = originalDataLayerPush(...args);
      for (const entry of args) {
        if (entry && typeof entry === "object") {
          handleDataLayerConsentReady(entry);
        }
      }
      return result;
    };
  }

  for (const entry of window.dataLayer) {
    handleDataLayerConsentReady(entry);
  }
}

/** @internal Resets module state between tests. */
export function resetConsentPageviewInstallForTests() {
  if (originalDataLayerPush && window.dataLayer) {
    window.dataLayer.push = originalDataLayerPush;
    originalDataLayerPush = null;
  }
  consentPageviewInstalled = false;
  pageviewFiredThisLoad = false;
}

/** Installs the consent pageview listener when theme.maestro.consentPandectesHost is true. */
export function installOptInConsentPageview() {
  if (!theme?.maestro?.consentPandectesHost || consentPageviewInstalled) {
    return;
  }
  consentPageviewInstalled = true;
  installDataLayerConsentReadyListener();
}
